import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FilePlus,
  FolderPlus,
  Loader2,
  Search,
  Star,
  Trash2,
} from "lucide-react";
import { api, type DocDocument, type DocFolder } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

function folderChildren(folders: DocFolder[], parentId: string | null): DocFolder[] {
  return folders.filter((f) => (f.parent_id || null) === parentId);
}

function FolderTree({
  folders,
  selectedId,
  onSelect,
  parentId = null,
  depth = 0,
}: {
  folders: DocFolder[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  parentId?: string | null;
  depth?: number;
}) {
  const kids = folderChildren(folders, parentId);
  return (
    <ul className="space-y-0.5">
      {kids.map((f) => (
        <li key={f.id}>
          <button
            type="button"
            onClick={() => onSelect(f.id)}
            className={cn(
              "flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm",
              selectedId === f.id ? "bg-indigo-100 text-indigo-900" : "hover:bg-slate-100",
            )}
            style={{ paddingLeft: 8 + depth * 12 }}
          >
            <span className="truncate">{f.name}</span>
            {f.system_key ? (
              <span className="ml-1 text-[10px] uppercase text-slate-400">sys</span>
            ) : null}
          </button>
          <FolderTree
            folders={folders}
            selectedId={selectedId}
            onSelect={onSelect}
            parentId={f.id}
            depth={depth + 1}
          />
        </li>
      ))}
    </ul>
  );
}

export default function Dokumenty() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [folderId, setFolderId] = useState<string | null>(null);
  const [starredOnly, setStarredOnly] = useState(false);
  const [tag, setTag] = useState<string>("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  // simple debounce via effect-like pattern in onChange timeout
  const [timer, setTimer] = useState<number | null>(null);
  const onSearch = (value: string) => {
    setQ(value);
    if (timer) window.clearTimeout(timer);
    const t = window.setTimeout(() => setQDebounced(value.trim()), 250);
    setTimer(t);
  };

  const foldersQ = useQuery({
    queryKey: ["docs-folders"],
    queryFn: () => api.docsFolders(),
  });
  const tagsQ = useQuery({
    queryKey: ["docs-tags"],
    queryFn: () => api.docsTags(),
  });
  const listQ = useQuery({
    queryKey: ["docs-list", folderId, starredOnly, tag, qDebounced],
    queryFn: () =>
      api.docsList({
        folder_id: folderId || undefined,
        starred: starredOnly || undefined,
        tag: tag || undefined,
        q: qDebounced || undefined,
      }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      api.docsCreate({
        title: "Nowy dokument",
        folder_id: folderId,
        body_md: "",
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["docs-list"] });
      nav(`/dokumenty/${res.document.id}`);
    },
  });

  const createFolderMut = useMutation({
    mutationFn: (name: string) => api.docsCreateFolder({ name, parent_id: folderId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["docs-folders"] }),
  });

  const starMut = useMutation({
    mutationFn: (doc: DocDocument) => api.docsPatch(doc.id, { starred: !doc.starred }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["docs-list"] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.docsDelete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["docs-list"] });
      void qc.invalidateQueries({ queryKey: ["docs-tags"] });
    },
  });

  const folders = foldersQ.data?.folders || [];
  const docs = listQ.data?.documents || [];
  const allTags = tagsQ.data?.tags || [];

  const folderName = useMemo(() => {
    if (!folderId) return "Wszystkie";
    return folders.find((f) => f.id === folderId)?.name || "Folder";
  }, [folderId, folders]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Dokumenty"
        subtitle="Notatki zespołu w Markdown. Foldery, tagi, gwiazdki. Osobno od katalogu Wiedza."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                const name = window.prompt("Nazwa folderu");
                if (name?.trim()) createFolderMut.mutate(name.trim());
              }}
            >
              <FolderPlus className="mr-1.5 h-4 w-4" />
              Folder
            </Button>
            <Button onClick={() => createMut.mutate()} disabled={createMut.isPending}>
              <FilePlus className="mr-1.5 h-4 w-4" />
              Nowy dokument
            </Button>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
        <aside className="space-y-3 rounded-xl border border-slate-200 bg-white p-3">
          <button
            type="button"
            className={cn(
              "w-full rounded-md px-2 py-1.5 text-left text-sm font-medium",
              !folderId && !starredOnly ? "bg-indigo-100 text-indigo-900" : "hover:bg-slate-100",
            )}
            onClick={() => {
              setFolderId(null);
              setStarredOnly(false);
            }}
          >
            Wszystkie
          </button>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm",
              starredOnly ? "bg-amber-100 text-amber-900" : "hover:bg-slate-100",
            )}
            onClick={() => {
              setStarredOnly(true);
              setFolderId(null);
            }}
          >
            <Star className="h-3.5 w-3.5 fill-current" />
            Gwiazdki
          </button>
          <div className="border-t border-slate-100 pt-2">
            <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              Foldery
            </p>
            {foldersQ.isLoading ? (
              <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" />
            ) : (
              <FolderTree
                folders={folders}
                selectedId={starredOnly ? null : folderId}
                onSelect={(id) => {
                  setFolderId(id);
                  setStarredOnly(false);
                }}
              />
            )}
          </div>
        </aside>

        <section className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                className="pl-8"
                placeholder="Szukaj w tytule i treści…"
                value={q}
                onChange={(e) => onSearch(e.target.value)}
              />
            </div>
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
            >
              <option value="">Wszystkie tagi</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-muted-foreground">
            {folderName}
            {starredOnly ? " · tylko gwiazdki" : ""}
            {tag ? ` · tag: ${tag}` : ""}
            {qDebounced ? ` · „${qDebounced}”` : ""}
          </p>

          {listQ.isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : docs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-12 text-center text-sm text-muted-foreground">
              Brak dokumentów. Utwórz nowy albo zmień filtry.
            </div>
          ) : (
            <ul className="space-y-2">
              {docs.map((doc) => (
                <li
                  key={doc.id}
                  className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-3 hover:border-indigo-200"
                >
                  <button
                    type="button"
                    className="mt-0.5 text-amber-500"
                    onClick={() => starMut.mutate(doc)}
                    title={doc.starred ? "Zdejmij gwiazdkę" : "Gwiazdka"}
                  >
                    <Star className={cn("h-4 w-4", doc.starred ? "fill-current" : "")} />
                  </button>
                  <Link to={`/dokumenty/${doc.id}`} className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{doc.title}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {doc.folder?.name || "—"} · {doc.updated_at}
                      {doc.source === "kanban" ? " · z Kanbana" : ""}
                    </div>
                    {doc.tags?.length ? (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {doc.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="text-[10px]">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => {
                      if (window.confirm(`Usunąć „${doc.title}”?`)) deleteMut.mutate(doc.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
